import { computed, readonly, ref, shallowRef } from "vue";
import { countValues, getRecordYear } from "../lib/content";
import type { ContentRecord } from "../types/content";

const records = shallowRef<ContentRecord[]>([]);
const loading = ref(false);
const error = ref("");
let request: Promise<void> | null = null;

function isContentRecord(value: unknown): value is ContentRecord {
  if (!value || typeof value !== "object") return false;
  const item = value as Partial<ContentRecord>;
  return Boolean(
    item.id &&
    item.title &&
    item.summary &&
    item.editorial_status &&
    Array.isArray(item.categories) &&
    Array.isArray(item.tags) &&
    Array.isArray(item.key_facts),
  );
}

async function load(): Promise<void> {
  if (records.value.length || request) return request ?? Promise.resolve();
  loading.value = true;
  error.value = "";
  request = fetch(`${import.meta.env.BASE_URL}data/public-content.json`)
    .then(async (response) => {
      if (!response.ok) throw new Error(`内容数据加载失败 (${response.status})`);
      const data: unknown = await response.json();
      if (!Array.isArray(data) || !data.every(isContentRecord)) {
        throw new Error("内容数据格式不正确");
      }
      records.value = data;
    })
    .catch((reason: unknown) => {
      error.value = reason instanceof Error ? reason.message : "内容数据加载失败";
      request = null;
    })
    .finally(() => {
      loading.value = false;
    });
  return request;
}

export function useContent() {
  const categories = computed(() => countValues(records.value.map((record) => record.categories)));
  const years = computed(() => {
    const counts = new Map<string, number>();
    records.value.forEach((record) => {
      const year = getRecordYear(record);
      counts.set(year, (counts.get(year) ?? 0) + 1);
    });
    return [...counts.entries()]
      .map(([label, count]) => ({ label, count }))
      .sort((left, right) => right.label.localeCompare(left.label));
  });

  return {
    records: computed(() => records.value),
    loading: readonly(loading),
    error: readonly(error),
    categories,
    years,
    load,
  };
}
